// src/pages/BlogPost.js
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, User, Tag, ArrowLeft, Share2, Copy, Check, BookOpen, ArrowRight, AlertTriangle, ChevronRight, BadgeCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import sanitizeHtml from '../utils/sanitizeHtml';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeHeading, setActiveHeading] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const colors = {
    primary: '#2055df',
    primaryDark: '#1a44b8',
    primaryLight: '#4d7df7',
    secondary: '#0f172a',
    accent: '#06b6d4',
    success: '#10b981',
    warning: '#f59e0b',
    bg: '#f8fafc',
    white: '#ffffff',
    gray100: '#f1f5f9',
    gray200: '#e2e8f0',
    gray300: '#cbd5e1',
    gray400: '#94a3b8',
    gray500: '#64748b',
    gray600: '#475569',
    gray700: '#334155',
    gray800: '#1e293b',
    gray900: '#0f172a'
  };

  const categoryColors = {
    'Savings Tips': '#10b981',
    'Drug Information': '#2055df',
    'Health & Wellness': '#06b6d4',
    'Insurance': '#8b5cf6',
    'Pharmacy': '#f59e0b',
    'Medicare': '#ef4444',
    'Generic Drugs': '#10b981',
    'Prescription Help': '#ec4899',
    default: '#2055df'
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const response = await axios.get(`/api/blog/post/${slug}`);
        const data = response.data.post || response.data;
        setPost(data);

        // Set page title and meta
        document.title = `${data.title} | Rx.ChatWithDr Blog`;
        const meta = document.querySelector('meta[name="description"]');
        const desc = data.metaDescription || data.excerpt || '';
        if (meta) {
          meta.setAttribute('content', desc);
        } else {
          const newMeta = document.createElement('meta');
          newMeta.name = 'description';
          newMeta.content = desc;
          document.head.appendChild(newMeta);
        }
      } catch (error) {
        console.error('Error fetching blog post:', error);
        if (error.response && error.response.status === 404) {
          setNotFound(true);
        } else {
          setNotFound(true);
        }
      } finally {
        setLoading(false);
      }
    };

    if (slug) fetchPost();
  }, [slug]);

  // Extract headings for table of contents
  const headings = useMemo(() => {
    if (!post || !post.content) return [];
    const regex = /<h2[^>]*(?:id="([^"]*)")?[^>]*>(.*?)<\/h2>/gi;
    const results = [];
    let match;
    while ((match = regex.exec(post.content)) !== null) {
      const text = match[2].replace(/<[^>]+>/g, '').trim();
      const id = match[1] || text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      results.push({ id, text });
    }
    return results;
  }, [post]);

  // Inject IDs into h2 tags in the content for anchor links
  const processedContent = useMemo(() => {
    if (!post || !post.content) return '';
    let content = post.content;
    content = content.replace(/<h2([^>]*)>(.*?)<\/h2>/gi, (fullMatch, attrs, inner) => {
      const text = inner.replace(/<[^>]+>/g, '').trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      if (attrs.includes('id=')) return fullMatch;
      return `<h2 id="${id}"${attrs}>${inner}</h2>`;
    });
    // Sanitize last so nothing injected above (or stored in the DB) can carry
    // scripts, event handlers or javascript: URLs into the page.
    return sanitizeHtml(content);
  }, [post]);

  // Track active heading for ToC highlight
  useEffect(() => {
    if (isMobile || headings.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length > 0) {
          setActiveHeading(visible[0].target.id);
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );
    headings.forEach(h => {
      const el = document.getElementById(h.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [headings, isMobile]);

  // Structured data
  useEffect(() => {
    if (!post) return;
    const existingScript = document.querySelector('script[data-blog-jsonld]');
    if (existingScript) existingScript.remove();

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: post.metaDescription || post.excerpt || '',
      image: post.featuredImage || undefined,
      datePublished: post.publishedAt || post.createdAt,
      dateModified: post.updatedAt || post.publishedAt || post.createdAt,
      author: {
        '@type': 'Organization',
        name: 'Rx.ChatWithDr Editorial',
        url: 'https://rx.chatwithdr.com'
      },
      reviewedBy: {
        '@type': 'Person',
        name: 'Healthcare Professional',
        jobTitle: 'Licensed Medical Reviewer'
      },
      publisher: {
        '@type': 'Organization',
        name: 'Rx.ChatWithDr',
        url: 'https://rx.chatwithdr.com'
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://rx.chatwithdr.com/blog/${slug}`
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-blog-jsonld', 'true');
    // Escape '<' so a title containing </script> can never terminate the block
    script.textContent = JSON.stringify(jsonLd).replace(/</g, '\\u003c');
    document.head.appendChild(script);

    return () => {
      const el = document.querySelector('script[data-blog-jsonld]');
      if (el) el.remove();
    };
  }, [post, slug]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getCategoryColor = (category) => {
    return categoryColors[category] || categoryColors.default;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const input = document.createElement('input');
      input.value = window.location.href;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt || post.metaDescription || '',
          url: window.location.href,
        });
      } catch {
        // User cancelled
      }
    } else {
      handleCopyLink();
    }
  };

  const scrollToHeading = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // -- Article content styles injected via wrapper --
  const articleStyles = {
    fontSize: 17,
    lineHeight: 1.8,
    color: colors.gray800,
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
  };

  // CSS for article content typography
  const articleCSS = `
    .blog-article-content h2 {
      font-size: 28px;
      font-weight: 700;
      color: ${colors.gray900};
      margin-top: 32px;
      margin-bottom: 16px;
      line-height: 1.3;
      scroll-margin-top: 80px;
    }
    .blog-article-content h3 {
      font-size: 22px;
      font-weight: 700;
      color: ${colors.gray900};
      margin-top: 24px;
      margin-bottom: 12px;
      line-height: 1.4;
    }
    .blog-article-content h4 {
      font-size: 18px;
      font-weight: 600;
      color: ${colors.gray800};
      margin-top: 20px;
      margin-bottom: 10px;
    }
    .blog-article-content p {
      font-size: 17px;
      line-height: 1.8;
      color: ${colors.gray700};
      margin-bottom: 16px;
    }
    .blog-article-content ul, .blog-article-content ol {
      padding-left: 24px;
      margin-bottom: 16px;
    }
    .blog-article-content li {
      font-size: 17px;
      line-height: 1.8;
      color: ${colors.gray700};
      margin-bottom: 8px;
    }
    .blog-article-content a {
      color: ${colors.primary};
      text-decoration: underline;
      text-decoration-color: ${colors.primary}40;
      text-underline-offset: 2px;
      transition: color 0.2s;
    }
    .blog-article-content a:hover {
      color: ${colors.primaryDark};
    }
    .blog-article-content strong {
      font-weight: 700;
      color: ${colors.gray900};
    }
    .blog-article-content blockquote {
      border-left: 4px solid ${colors.primary};
      background: ${colors.gray100};
      padding: 16px 20px;
      margin: 24px 0;
      border-radius: 0 12px 12px 0;
      font-style: italic;
      color: ${colors.gray600};
    }
    .blog-article-content blockquote p {
      margin-bottom: 0;
      color: ${colors.gray600};
    }
    .blog-article-content img {
      max-width: 100%;
      height: auto;
      border-radius: 12px;
      margin: 20px 0;
    }
    .blog-article-content table {
      width: 100%;
      border-collapse: collapse;
      margin: 20px 0;
    }
    .blog-article-content th, .blog-article-content td {
      padding: 12px 16px;
      border: 1px solid ${colors.gray200};
      text-align: left;
      font-size: 15px;
    }
    .blog-article-content th {
      background: ${colors.gray100};
      font-weight: 600;
      color: ${colors.gray800};
    }
    .blog-article-content pre {
      background: ${colors.gray900};
      color: #e2e8f0;
      padding: 20px;
      border-radius: 12px;
      overflow-x: auto;
      margin: 20px 0;
      font-size: 14px;
      line-height: 1.6;
    }
    .blog-article-content code {
      background: ${colors.gray100};
      padding: 2px 6px;
      border-radius: 4px;
      font-size: 15px;
      color: ${colors.primary};
    }
    .blog-article-content pre code {
      background: none;
      padding: 0;
      color: inherit;
    }
  `;

  // -- Loading State --
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: colors.bg }}>
        <div style={{ maxWidth: 800, margin: '0 auto', padding: isMobile ? '32px 16px' : '48px 20px' }}>
          <div style={{ marginBottom: 32 }}>
            <div style={{ width: 100, height: 28, borderRadius: 14, background: colors.gray200, marginBottom: 16 }} />
            <div style={{ width: '80%', height: 36, borderRadius: 8, background: colors.gray200, marginBottom: 12 }} />
            <div style={{ width: '60%', height: 36, borderRadius: 8, background: colors.gray200, marginBottom: 24 }} />
            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ width: 120, height: 16, borderRadius: 4, background: colors.gray100 }} />
              <div style={{ width: 100, height: 16, borderRadius: 4, background: colors.gray100 }} />
              <div style={{ width: 80, height: 16, borderRadius: 4, background: colors.gray100 }} />
            </div>
          </div>
          <div style={{ width: '100%', height: 400, borderRadius: 16, background: colors.gray200, marginBottom: 40 }} />
          {[...Array(8)].map((_, i) => (
            <div key={i} style={{
              width: `${70 + Math.random() * 30}%`,
              height: 16,
              borderRadius: 4,
              background: colors.gray100,
              marginBottom: 14,
            }} />
          ))}
        </div>
      </div>
    );
  }

  // -- 404 State --
  if (notFound || !post) {
    return (
      <div style={{ minHeight: '100vh', background: colors.bg }}>
        <div style={{
          maxWidth: 600,
          margin: '0 auto',
          padding: '80px 20px',
          textAlign: 'center',
        }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <BookOpen size={56} color={colors.gray300} style={{ marginBottom: 20 }} />
            <h1 style={{ fontSize: 28, fontWeight: 800, color: colors.gray800, marginBottom: 12 }}>
              Article Not Found
            </h1>
            <p style={{ fontSize: 16, color: colors.gray500, marginBottom: 32, lineHeight: 1.6 }}>
              The blog post you are looking for does not exist or may have been removed.
            </p>
            <Link to="/blog" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: colors.primary,
              color: colors.white,
              padding: '12px 28px',
              borderRadius: 12,
              fontWeight: 600,
              fontSize: 15,
              textDecoration: 'none',
            }}>
              <ArrowLeft size={18} /> Back to Blog
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: colors.bg }}>
      <style>{articleCSS}</style>

      {/* Breadcrumb */}
      <div style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: isMobile ? '16px 16px 0' : '20px 20px 0',
      }}>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <Link to="/" style={{ fontSize: 13, color: colors.gray400, textDecoration: 'none' }}>Home</Link>
          <ChevronRight size={14} color={colors.gray300} />
          <Link to="/blog" style={{ fontSize: 13, color: colors.gray400, textDecoration: 'none' }}>Blog</Link>
          <ChevronRight size={14} color={colors.gray300} />
          <span style={{ fontSize: 13, color: colors.gray600, fontWeight: 500 }}>
            {post.title && post.title.length > 40 ? post.title.substring(0, 40) + '...' : post.title}
          </span>
        </nav>
      </div>

      <div style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: isMobile ? '16px 16px 48px' : '24px 20px 64px',
        display: 'flex',
        gap: 40,
        alignItems: 'flex-start',
      }}>
        {/* Main Content */}
        <article style={{ flex: 1, minWidth: 0, maxWidth: 800 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Post Header */}
            <header style={{ marginBottom: 32 }}>
              {/* Category Badge */}
              {post.category && (
                <span style={{
                  display: 'inline-block',
                  background: getCategoryColor(post.category) + '15',
                  color: getCategoryColor(post.category),
                  padding: '6px 14px',
                  borderRadius: 20,
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 16,
                }}>
                  {post.category}
                </span>
              )}

              {/* Title */}
              <h1 style={{
                fontSize: isMobile ? 28 : 40,
                fontWeight: 800,
                color: colors.gray900,
                lineHeight: 1.2,
                marginBottom: 20,
              }}>
                {post.title}
              </h1>

              {/* Meta info */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: isMobile ? 12 : 20,
                marginBottom: 16,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <User size={16} color={colors.white} />
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 600, color: colors.gray700 }}>
                    Rx.ChatWithDr Editorial
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Calendar size={15} color={colors.gray400} />
                  <span style={{ fontSize: 14, color: colors.gray500 }}>
                    {formatDate(post.publishedAt || post.createdAt)}
                  </span>
                </div>
                {post.readingTime && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Clock size={15} color={colors.gray400} />
                    <span style={{ fontSize: 14, color: colors.gray500 }}>
                      {post.readingTime} min read
                    </span>
                  </div>
                )}
              </div>

              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
                  {post.tags.map((tag, i) => (
                    <span key={i} style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 12,
                      color: colors.gray500,
                      background: colors.gray100,
                      padding: '4px 10px',
                      borderRadius: 6,
                    }}>
                      <Tag size={11} />
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Medically Reviewed Badge */}
              <div style={{
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: 12,
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}>
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: colors.success,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <BadgeCheck size={18} color={colors.white} />
                </div>
                <span style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#065f46',
                }}>
                  Medically reviewed by Healthcare Professional
                </span>
              </div>
            </header>

            {/* Featured Image */}
            {post.featuredImage && (
              <div style={{
                width: '100%',
                borderRadius: 16,
                overflow: 'hidden',
                marginBottom: 40,
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              }}>
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    maxHeight: 500,
                    objectFit: 'cover',
                  }}
                />
              </div>
            )}

            {/* Share buttons */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 32,
              paddingBottom: 24,
              borderBottom: `1px solid ${colors.gray200}`,
            }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: colors.gray500 }}>Share:</span>
              <button
                onClick={handleShare}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: `1px solid ${colors.gray200}`,
                  background: colors.white,
                  cursor: 'pointer',
                  fontSize: 13,
                  color: colors.gray600,
                  fontWeight: 500,
                  transition: 'all 0.2s ease',
                }}
              >
                <Share2 size={14} /> Share
              </button>
              <button
                onClick={handleCopyLink}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: `1px solid ${copied ? colors.success + '40' : colors.gray200}`,
                  background: copied ? colors.success + '08' : colors.white,
                  cursor: 'pointer',
                  fontSize: 13,
                  color: copied ? colors.success : colors.gray600,
                  fontWeight: 500,
                  transition: 'all 0.2s ease',
                }}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
            </div>

            {/* Article Content */}
            <div
              className="blog-article-content"
              style={articleStyles}
              dangerouslySetInnerHTML={{ __html: processedContent }}
            />

            {/* Medical Disclaimer */}
            <div style={{
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 16,
              padding: isMobile ? 20 : 24,
              marginTop: 40,
              marginBottom: 40,
              display: 'flex',
              gap: 16,
              alignItems: 'flex-start',
            }}>
              <AlertTriangle size={24} color={colors.warning} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <h4 style={{ fontSize: 15, fontWeight: 700, color: colors.gray800, marginBottom: 6 }}>
                  Medical Disclaimer
                </h4>
                <p style={{ fontSize: 14, color: colors.gray600, lineHeight: 1.7, margin: 0 }}>
                  This article is for informational purposes only and does not constitute medical advice.
                  Always consult a healthcare professional before making decisions about your medications
                  or treatment. Rx.ChatWithDr is not a substitute for professional medical advice,
                  diagnosis, or treatment.
                </p>
              </div>
            </div>

            {/* Author Bio */}
            <div style={{
              background: colors.white,
              border: `1px solid ${colors.gray200}`,
              borderRadius: 16,
              padding: isMobile ? 20 : 28,
              marginBottom: 40,
              display: 'flex',
              gap: 20,
              alignItems: isMobile ? 'flex-start' : 'center',
              flexDirection: isMobile ? 'column' : 'row',
            }}>
              <div style={{
                width: 64,
                height: 64,
                borderRadius: 16,
                background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <User size={28} color={colors.white} />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.gray900, marginBottom: 6 }}>
                  Rx.ChatWithDr Editorial
                </h3>
                <p style={{ fontSize: 14, color: colors.gray500, lineHeight: 1.6, margin: 0 }}>
                  Our editorial team is dedicated to helping Americans save money on their prescriptions.
                  We research and write evidence-based articles about prescription savings, generic alternatives,
                  pharmacy tips, and navigating the healthcare system.
                </p>
              </div>
            </div>

            {/* Related Posts */}
            {post.relatedPosts && post.relatedPosts.length > 0 && (
              <div style={{ marginBottom: 40 }}>
                <h2 style={{ fontSize: 22, fontWeight: 700, color: colors.gray900, marginBottom: 20 }}>
                  Related Articles
                </h2>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                  gap: 16,
                }}>
                  {post.relatedPosts.slice(0, 4).map((related, i) => (
                    <Link
                      key={i}
                      to={`/blog/${related.slug}`}
                      style={{
                        textDecoration: 'none',
                        background: colors.white,
                        borderRadius: 12,
                        border: `1px solid ${colors.gray200}`,
                        padding: 16,
                        display: 'flex',
                        gap: 14,
                        alignItems: 'center',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <div style={{
                        width: 56,
                        height: 56,
                        borderRadius: 10,
                        flexShrink: 0,
                        background: related.featuredImage
                          ? `url(${related.featuredImage}) center/cover no-repeat`
                          : `linear-gradient(135deg, ${colors.primary}15, ${colors.accent}15)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        {!related.featuredImage && <BookOpen size={20} color={colors.primary} style={{ opacity: 0.4 }} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h4 style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: colors.gray800,
                          lineHeight: 1.4,
                          marginBottom: 4,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>
                          {related.title}
                        </h4>
                        {related.readingTime && (
                          <span style={{ fontSize: 12, color: colors.gray400 }}>{related.readingTime} min read</span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* CTA Section */}
            <div style={{
              background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
              borderRadius: 20,
              padding: isMobile ? '32px 20px' : '40px 40px',
              textAlign: 'center',
              marginBottom: 32,
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute',
                top: -30,
                right: -30,
                width: 140,
                height: 140,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.06)',
              }} />
              <h2 style={{
                fontSize: isMobile ? 24 : 28,
                fontWeight: 800,
                color: colors.white,
                marginBottom: 12,
                position: 'relative',
              }}>
                Start Saving Today
              </h2>
              <p style={{
                fontSize: 16,
                color: 'rgba(255,255,255,0.85)',
                lineHeight: 1.6,
                marginBottom: 24,
                maxWidth: 480,
                marginLeft: 'auto',
                marginRight: 'auto',
                position: 'relative',
              }}>
                Get your free prescription discount card and save up to 80% on medications at over 67,000 pharmacies.
              </p>
              <Link to="/card" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: colors.white,
                color: colors.primary,
                padding: '14px 32px',
                borderRadius: 12,
                fontWeight: 700,
                fontSize: 16,
                textDecoration: 'none',
                position: 'relative',
                transition: 'all 0.2s ease',
              }}>
                Get Free Card <ArrowRight size={18} />
              </Link>
            </div>

            {/* Back to Blog */}
            <div style={{ textAlign: 'center' }}>
              <Link to="/blog" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                color: colors.primary,
                fontWeight: 600,
                fontSize: 15,
                textDecoration: 'none',
              }}>
                <ArrowLeft size={18} /> Back to All Articles
              </Link>
            </div>
          </motion.div>
        </article>

        {/* Table of Contents Sidebar - Desktop only */}
        {!isMobile && headings.length > 0 && (
          <aside style={{
            width: 260,
            flexShrink: 0,
            position: 'sticky',
            top: 80,
            maxHeight: 'calc(100vh - 100px)',
            overflowY: 'auto',
          }}>
            <div style={{
              background: colors.white,
              borderRadius: 16,
              padding: 24,
              border: `1px solid ${colors.gray200}`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}>
              <h3 style={{
                fontSize: 13,
                fontWeight: 700,
                color: colors.gray400,
                textTransform: 'uppercase',
                letterSpacing: 1,
                marginBottom: 16,
              }}>
                Table of Contents
              </h3>
              <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {headings.map((h, i) => (
                  <button
                    key={i}
                    onClick={() => scrollToHeading(h.id)}
                    style={{
                      background: activeHeading === h.id ? colors.primary + '08' : 'transparent',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: 8,
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: 13,
                      lineHeight: 1.5,
                      fontWeight: activeHeading === h.id ? 600 : 400,
                      color: activeHeading === h.id ? colors.primary : colors.gray500,
                      borderLeft: activeHeading === h.id
                        ? `3px solid ${colors.primary}`
                        : '3px solid transparent',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {h.text}
                  </button>
                ))}
              </nav>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

export default BlogPost;
